// ✅ src/pages/VehicleV2/JobCardEntryDetail.jsx  (UPDATED DESIGN TEMPLATE)
// Backend expected:
//   app.use("/api/jobCardEntryDetail", jobCardEntryDetail);
//   app.use("/api/jobCardEntryDetails", jobCardEntryDetail); // if mounted plural
//
// APIs used:
//   GET  /api/jobCardEntryDetails/init
//   POST /api/jobCardEntryDetails/submit
//
// SP duplicate columns (fld_jobcardentryid, fld_vehicleid) arrive as arrays.
// Detail PK = fld_jobcardentrydetid
// Parent card = fld_jobcardentryid
// Spare column = fld_sparedetails

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave, FaUndo } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/jobCardEntryDetails";

const firstVal = (v) => {
  if (Array.isArray(v)) {
    for (const x of v) {
      if (x !== undefined && x !== null && String(x).trim() !== "") return x;
    }
    return v[0];
  }
  return v;
};

const toText = (v, def = "") => {
  const raw = firstVal(v);
  if (raw === null || raw === undefined) return def;
  if (raw instanceof Date) return formatDDMMYYYY(raw);
  return String(raw);
};

const toInt = (v, def = 0) => {
  const n = Number(firstVal(v));
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(firstVal(v) ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");

const parseDDMMYYYY = (s) => {
  const parts = String(s || "").split("/");
  if (parts.length !== 3) return null;

  const [dd, mm, yyyy] = parts;
  const d = Number(dd);
  const m = Number(mm);
  const y = Number(yyyy);

  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) {
    return null;
  }

  const dt = new Date(y, m - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }

  return dt;
};

const formatDDMMYYYY = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
  return `${pad2(dateObj.getDate())}/${pad2(
    dateObj.getMonth() + 1
  )}/${dateObj.getFullYear()}`;
};

const parseAnyDateToDDMMYYYY = (raw) => {
  const val = firstVal(raw);
  if (val instanceof Date) return formatDDMMYYYY(val);

  const text = clean(val);
  if (!text) return "";

  if (
    text.startsWith("Sat ") ||
    text.startsWith("Sun ") ||
    text.startsWith("Mon ") ||
    text.startsWith("Tue ") ||
    text.startsWith("Wed ") ||
    text.startsWith("Thu ") ||
    text.startsWith("Fri ")
  ) {
    const dt = new Date(text);
    if (!Number.isNaN(dt.getTime())) return formatDDMMYYYY(dt);
  }

  const ddmmyyyy = parseDDMMYYYY(text);
  if (ddmmyyyy) return formatDDMMYYYY(ddmmyyyy);

  const dt = new Date(text);
  if (!Number.isNaN(dt.getTime())) return formatDDMMYYYY(dt);

  return text;
};

const JobCardEntryDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid") || "8";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const branchId = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim() || "HO",
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") ||
    userId ||
    "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const vehicleId = useMemo(() => {
    const sp = new URLSearchParams(location?.search || "");
    const fromQuery = toInt(sp.get("vehicleId") || sp.get("vehicleid"));
    if (fromQuery) return fromQuery;
    return toInt(getLS("vehicleId") || 0);
  }, [location]);

  const [pageLoading, setPageLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [headerInfo, setHeaderInfo] = useState({
    jobCardNo: "",
    vehicleName: "",
    vehicleRegNo: "",
    vehicleCode: "",
    vehicleModel: "",
  });

  const [jobcards, setJobcards] = useState([]);
  const [details, setDetails] = useState([]);

  const [page1, setPage1] = useState(1);
  const [limit1, setLimit1] = useState(10);
  const [page2, setPage2] = useState(1);
  const [limit2, setLimit2] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const readonlyBox = `w-full px-4 py-2 rounded-lg border font-medium ${
    isDark
      ? "bg-gray-700/40 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const smallInputClass = `w-full px-2 py-1 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const isBusy = pageLoading || saving;

  const mapHeader = useCallback((data) => {
    const h = data?.header || data || {};
    return {
      jobCardNo: toText(h?.jobCardNo ?? h?.fld_jobcardno),
      vehicleName: toText(h?.vehicleName ?? h?.fld_vehicle_name),
      vehicleRegNo: toText(h?.vehicleRegNo ?? h?.fld_vehicle_regno),
      vehicleCode: toText(h?.vehicleCode ?? h?.fld_vehicle_code),
      vehicleModel: toText(h?.vehicleModel ?? h?.fld_vehicle_model),
    };
  }, []);

  const mapTopRow = useCallback(
    (r) => {
      const raw = r?._raw || r || {};
      const jobCardEntryId = toInt(
        r?.jobCardEntryId ??
          r?.fld_jobcardentryid ??
          raw?.fld_jobcardentryid ??
          r?.id,
        0
      );

      const spareRaw =
        r?.sparesCount ??
        r?.fld_spares ??
        r?.fld_sparedetails ??
        r?.spareDetails ??
        raw?.fld_sparedetails ??
        0;

      return {
        jobCardEntryId,
        jobCardEntryDetId: toInt(
          r?.fld_jobcardentrydetid ?? raw?.fld_jobcardentrydetid,
          0
        ),
        vehicleId: toInt(
          r?.vehicleId ?? r?.fld_vehicleid ?? raw?.fld_vehicleid ?? vehicleId,
          0
        ),
        jobCardNo: toText(r?.jobCardNo ?? r?.fld_jobcardno),
        entryDate: parseAnyDateToDDMMYYYY(
          r?.entryDate ?? r?.fld_entrydate1 ?? r?.fld_entrydate
        ),
        vehicleName: toText(r?.vehicleName ?? r?.fld_vehicle_name),
        vehicleRegNo: toText(r?.vehicleRegNo ?? r?.fld_vehicle_regno),
        location: toText(r?.location ?? r?.fld_Worklocationname),
        hrKm: toText(
          r?.hrKm ?? r?.fld_hrs ?? r?.fld_hrmtrreading ?? r?.fld_workinghrs
        ),
        status: toText(r?.status ?? r?.fld_status),
        natureOfComplaint: toText(
          r?.natureOfComplaint ?? r?.fld_natureofcomplaint
        ),
        complaintAttendedBy: toText(
          r?.complaintAttendedBy ?? r?.fld_complaintattendedby
        ),
        sparesCount: toInt(spareRaw, 0),
        spareDetails: toText(
          r?.spareDetails ?? r?.fld_sparedetails ?? spareRaw ?? "0"
        ),
        inspectedBy: toText(r?.inspectedBy ?? r?.fld_inspectedby),
        remedy: toText(r?.remedy ?? r?.fld_remedy),
        fromDate: parseAnyDateToDDMMYYYY(
          r?.fromDate ?? r?.fld_maintainfromdate1 ?? r?.fld_maintainfromdate
        ),
        inTime: toText(r?.inTime ?? r?.fld_maintainintime),
        toDate: parseAnyDateToDDMMYYYY(
          r?.toDate ?? r?.fld_maintaintodate1 ?? r?.fld_maintaintodate
        ),
        outTime: toText(r?.outTime ?? r?.fld_maintainouttime),
        hrs: toText(r?.hrs ?? r?.fld_maintainhrs),
        maintenanceBy: toText(r?.maintenanceBy ?? r?.fld_maintenanceby),
        _raw: raw,
      };
    },
    [vehicleId]
  );

  const mapDetailRow = useCallback(
    (r) => {
      const raw = r?._raw || r || {};
      const jobCardEntryDetId = toInt(
        r?.fld_jobcardentrydetid ?? raw?.fld_jobcardentrydetid ?? r?.id,
        0
      );
      const jobCardEntryId = toInt(
        r?.jobCardEntryId ??
          r?.fld_jobcardentryid ??
          raw?.fld_jobcardentryid,
        0
      );

      return {
        jobCardEntryDetId,
        jobCardEntryId,
        vehicleId: toInt(
          r?.vehicleId ?? r?.fld_vehicleid ?? raw?.fld_vehicleid ?? vehicleId,
          0
        ),
        jobCardNo: toText(r?.jobCardNo ?? r?.fld_jobcardno),
        entryDate: parseAnyDateToDDMMYYYY(
          r?.entryDate ?? r?.fld_entrydate1 ?? r?.fld_entrydate
        ),
        vehicleName: toText(r?.vehicleName ?? r?.fld_vehicle_name),
        vehicleRegNo: toText(r?.vehicleRegNo ?? r?.fld_vehicle_regno),
        location: toText(r?.location ?? r?.fld_Worklocationname),
        hrKm: toText(
          r?.hrKm ?? r?.fld_hrs ?? r?.fld_hrmtrreading ?? r?.fld_workinghrs
        ),
        status: toText(r?.status ?? r?.fld_status),
        natureOfComplaint: toText(
          r?.natureOfComplaint ?? r?.fld_natureofcomplaint
        ),
        complaintAttendedBy: toText(
          r?.complaintAttendedBy ?? r?.fld_complaintattendedby
        ),
        inspectedBy: toText(r?.inspectedBy ?? r?.fld_inspectedby),
        remedy: toText(r?.remedy ?? r?.fld_remedy),
        fromDate: parseAnyDateToDDMMYYYY(
          r?.fromDate ?? r?.fld_maintainfromdate1 ?? r?.fld_maintainfromdate
        ),
        inTime: toText(r?.inTime ?? r?.fld_maintainintime),
        toDate: parseAnyDateToDDMMYYYY(
          r?.toDate ?? r?.fld_maintaintodate1 ?? r?.fld_maintaintodate
        ),
        outTime: toText(r?.outTime ?? r?.fld_maintainouttime),
        hrs: toText(r?.hrs ?? r?.fld_maintainhrs),
        maintenanceBy: toText(r?.maintenanceBy ?? r?.fld_maintenanceby),
        spareDetails: toText(
          r?.spareDetails ?? r?.fld_sparedetails ?? r?.fld_spares ?? "View"
        ),
        _raw: raw,
      };
    },
    [vehicleId]
  );

  const loadInit = useCallback(async () => {
    try {
      if (!toInt(vehicleId, 0)) {
        toast.warn("vehicleId not found");
        return;
      }

      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          branchType: clean(branchType),
          vehicleId: toInt(vehicleId, 0),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setHeaderInfo(mapHeader(data));

      const bottomRows = Array.isArray(data?.details)
        ? data.details.map(mapDetailRow)
        : [];

      let topRows = Array.isArray(data?.jobcards)
        ? data.jobcards.map(mapTopRow)
        : [];

      // view SP returned [] — seed top grid from unique parent cards in details
      if (topRows.length === 0 && bottomRows.length > 0) {
        const seen = new Set();
        topRows = [];
        for (const d of bottomRows) {
          const entryId = toInt(d.jobCardEntryId);
          if (!entryId || seen.has(entryId)) continue;
          seen.add(entryId);
          topRows.push({
            ...d,
            inspectedBy: "",
            remedy: "",
            fromDate: "",
            inTime: "",
            toDate: "",
            outTime: "",
            hrs: "",
            maintenanceBy: "",
          });
        }
      }

      setJobcards(topRows);
      setDetails(bottomRows);
      setSelectedIds([]);
      setSelectAll(false);
      setPage1(1);
      setPage2(1);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Init failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    branchType,
    vehicleId,
    commonHeaders,
    mapHeader,
    mapTopRow,
    mapDetailRow,
  ]);

  const totalPages1 = useMemo(() => {
    const t = Math.ceil(jobcards.length / limit1);
    return t > 0 ? t : 1;
  }, [jobcards.length, limit1]);

  const totalPages2 = useMemo(() => {
    const t = Math.ceil(details.length / limit2);
    return t > 0 ? t : 1;
  }, [details.length, limit2]);

  const pageRows1 = useMemo(() => {
    const start = (page1 - 1) * limit1;
    return jobcards.slice(start, start + limit1);
  }, [jobcards, page1, limit1]);

  const pageRows2 = useMemo(() => {
    const start = (page2 - 1) * limit2;
    return details.slice(start, start + limit2);
  }, [details, page2, limit2]);

  const paginationText1 = useMemo(() => {
    const total = jobcards.length;
    const from = total === 0 ? 0 : (page1 - 1) * limit1 + 1;
    const to = Math.min(total, page1 * limit1);
    return `${from}-${to} of ${total}`;
  }, [jobcards.length, page1, limit1]);

  const paginationText2 = useMemo(() => {
    const total = details.length;
    const from = total === 0 ? 0 : (page2 - 1) * limit2 + 1;
    const to = Math.min(total, page2 * limit2);
    return `${from}-${to} of ${total}`;
  }, [details.length, page2, limit2]);

  const canPrev1 = page1 > 1;
  const canNext1 = page1 < totalPages1;
  const canPrev2 = page2 > 1;
  const canNext2 = page2 < totalPages2;

  const toggleSelect = useCallback((jobCardEntryId) => {
    setSelectedIds((prev) =>
      prev.includes(jobCardEntryId)
        ? prev.filter((x) => x !== jobCardEntryId)
        : [...prev, jobCardEntryId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const idsOnPage = pageRows1.map((r) => r.jobCardEntryId).filter(Boolean);

    if (selectAll) {
      setSelectedIds((prev) => prev.filter((id) => !idsOnPage.includes(id)));
      setSelectAll(false);
    } else {
      setSelectedIds((prev) => {
        const set = new Set(prev);
        idsOnPage.forEach((id) => set.add(id));
        return Array.from(set);
      });
      setSelectAll(true);
    }
  }, [pageRows1, selectAll]);

  useEffect(() => {
    const idsOnPage = pageRows1.map((r) => r.jobCardEntryId).filter(Boolean);
    setSelectAll(
      idsOnPage.length > 0 && idsOnPage.every((id) => selectedIds.includes(id))
    );
  }, [pageRows1, selectedIds]);

  const updateRowField = useCallback((jobCardEntryId, field, value) => {
    setJobcards((prev) =>
      prev.map((r) =>
        r.jobCardEntryId === jobCardEntryId ? { ...r, [field]: value } : r
      )
    );
  }, []);

  const validateSelectedRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      toast.warn("Please select at least one row");
      return false;
    }

    for (const row of rows) {
      if (!toInt(row.jobCardEntryId)) {
        toast.warn("JobCardEntryId missing on a selected row");
        return false;
      }
      if (!clean(row.inspectedBy)) {
        toast.warn(`Please enter Inspected By for ${row.jobCardNo || "row"}`);
        return false;
      }
      if (!clean(row.remedy)) {
        toast.warn(`Please enter Remedy for ${row.jobCardNo || "row"}`);
        return false;
      }
      if (!parseDDMMYYYY(row.fromDate)) {
        toast.warn(`Invalid From Date for Job Card No ${row.jobCardNo || ""}`);
        return false;
      }
      if (!clean(row.inTime)) {
        toast.warn(`Please enter IN Time for ${row.jobCardNo || "row"}`);
        return false;
      }
      if (!parseDDMMYYYY(row.toDate)) {
        toast.warn(`Invalid To Date for Job Card No ${row.jobCardNo || ""}`);
        return false;
      }
      if (!clean(row.outTime)) {
        toast.warn(`Please enter Out Time for ${row.jobCardNo || "row"}`);
        return false;
      }
      if (!clean(row.hrs) || !Number.isFinite(Number(row.hrs))) {
        toast.warn(`Please enter Hrs for ${row.jobCardNo || "row"}`);
        return false;
      }
      if (!clean(row.maintenanceBy)) {
        toast.warn(`Please enter Maintenance By for ${row.jobCardNo || "row"}`);
        return false;
      }
    }

    return true;
  }, []);

  const handleSubmit = async () => {
    try {
      const selectedRows = jobcards.filter((r) =>
        selectedIds.includes(r.jobCardEntryId)
      );

      if (!validateSelectedRows(selectedRows)) return;

      setSaving(true);

      const body = {
        loginUserId: toInt(loginUserId, 0),
        branchId: toInt(branchId, 0),
        branchType: clean(branchType),
        vehicleId: toInt(vehicleId, 0),
        rows: selectedRows.map((r) => ({
          jobCardEntryId: toInt(r.jobCardEntryId, 0),
          fld_jobcardentryid: toInt(r.jobCardEntryId, 0),
          inspectedBy: clean(r.inspectedBy),
          remedy: clean(r.remedy),
          fromDate: clean(r.fromDate),
          inTime: clean(r.inTime),
          toDate: clean(r.toDate),
          outTime: clean(r.outTime),
          hrs: clean(r.hrs),
          spare: String(toInt(r.sparesCount, 0)),
          fld_sparedetails: String(toInt(r.sparesCount, 0)),
          maintenanceBy: clean(r.maintenanceBy),
        })),
      };

      const { data } = await axios.post(`${API_BASE}/submit`, body, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Submit failed");
      }

      toast.success(data?.message || "Submitted successfully");
      await loadInit();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Submit failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = useCallback(() => {
    setJobcards((prev) =>
      prev.map((r) => ({
        ...r,
        inspectedBy: "",
        remedy: "",
        fromDate: "",
        inTime: "",
        toDate: "",
        outTime: "",
        hrs: "",
        maintenanceBy: "",
      }))
    );
    setSelectedIds([]);
    setSelectAll(false);
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSpareDetails = useCallback(
    (row) => {
      const raw = row?._raw || {};
      const id = toInt(
        row?.jobCardEntryDetId ??
         
        0
      );
      const vid = toInt(
        row?.vehicleId ?? raw?.fld_vehicleid ?? vehicleId,
        0
      );

      if (!id) {
        toast.error("jobcardid is not found");
        console.warn("Spare click missing jobCardEntryId", row);
        console.log("id", id);
        return;
      }

      try {
        localStorage.setItem("jobCardEntryId", String(id));
        localStorage.setItem("jobcardentryid", String(id));
        localStorage.setItem("jobCardId", String(id));
        localStorage.setItem("jobcardid", String(id));
        if (vid) localStorage.setItem("vehicleId", String(vid));
      } catch {
        /* ignore quota / private mode */
      }

      const qs = new URLSearchParams({
        jobCardEntryId: String(id),
        jobcardentryid: String(id),
        jobCardId: String(id),
        jobcardid: String(id),
        vehicleId: String(vid || ""),
        vehicleid: String(vid || ""),
      });

      navigate(`/vehicle/SparesDetails?${qs.toString()}`);
    },
    [navigate, vehicleId]
  );

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const pagerBtn = (enabled) =>
    `px-4 py-2 rounded-lg border text-sm font-semibold ${
      enabled
        ? isDark
          ? "border-gray-600 text-gray-200 hover:bg-gray-700"
          : "border-gray-300 text-gray-700 hover:bg-white"
        : "opacity-50 cursor-not-allowed border-gray-300"
    }`;

  const cellClass = `px-6 py-4 text-sm ${
    isDark ? "text-gray-300" : "text-gray-900"
  }`;

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">JOB CARD ENTRY DETAIL</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleBack}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>

              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Job Card Entry Detail
              </h2>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleReset}
                type="button"
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-white"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaUndo />
                Reset
              </button>

              <button
                onClick={handleSubmit}
                disabled={isBusy || selectedIds.length === 0}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isBusy || selectedIds.length === 0
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                } ${buttonClass} ${hoverClass} text-white`}
              >
                <FaSave />
                {saving ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>

          <div
            className={`p-6 rounded-lg mb-6 ${
              isDark ? "bg-gray-800/50" : "bg-blue-50"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                isDark ? "text-white" : "text-blue-900"
              }`}
            >
              Vehicle Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Job Card No</label>
                <div className={readonlyBox}>{headerInfo.jobCardNo || "-"}</div>
              </div>
              <div>
                <label className={labelClass}>Vehicle Name</label>
                <div className={readonlyBox}>
                  {headerInfo.vehicleName || "-"}
                </div>
              </div>
              <div>
                <label className={labelClass}>Vehicle Reg No</label>
                <div className={readonlyBox}>
                  {headerInfo.vehicleRegNo || "-"}
                </div>
              </div>
              <div>
                <label className={labelClass}>Vehicle Code</label>
                <div className={readonlyBox}>
                  {headerInfo.vehicleCode || "-"}
                </div>
              </div>
              <div>
                <label className={labelClass}>Vehicle Model</label>
                <div className={readonlyBox}>
                  {headerInfo.vehicleModel || "-"}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading && jobcards.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : pageRows1.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No Records found
                </div>
              </div>
            ) : (
              <>
                <table
                  className={`w-full min-w-[2600px] ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        S.No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Job Card No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Entry Date
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Reg No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Location
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Hr/Km
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Nature Of Complaint
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Complaint Attended By
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Inspected By
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Remedy
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        From Date
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        IN Time
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        To Date
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Out Time
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Hrs
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Maintenance By
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">
                        Spare Details
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          disabled={isBusy}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    } divide-y`}
                  >
                    {pageRows1.map((row, idx) => {
                      const rid = row.jobCardEntryId;
                      return (
                        <tr
                          key={rid || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td className={cellClass}>
                            {(page1 - 1) * limit1 + idx + 1}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-semibold ${
                              isDark ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            {row.jobCardNo || "-"}
                          </td>
                          <td className={cellClass}>{row.entryDate || "-"}</td>
                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleName || "-"}
                          </td>
                          <td className={cellClass}>
                            {row.vehicleRegNo || "-"}
                          </td>
                          <td className={cellClass}>{row.location || "-"}</td>
                          <td className={cellClass}>{row.hrKm || "-"}</td>
                          <td className="px-6 py-4 text-sm">
                            {row.status ? (
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  isDark
                                    ? "bg-yellow-900/40 text-yellow-200"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {row.status}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className={cellClass}>
                            {row.natureOfComplaint || "-"}
                          </td>
                          <td className={cellClass}>
                            {row.complaintAttendedBy || "-"}
                          </td>
                          <td className="px-3 py-3 min-w-[180px]">
                            <input
                              type="text"
                              value={row.inspectedBy}
                              onChange={(e) =>
                                updateRowField(
                                  rid,
                                  "inspectedBy",
                                  e.target.value
                                )
                              }
                              className={smallInputClass}
                              placeholder="Inspected By"
                              disabled={isBusy}
                            />
                          </td>
                          <td className="px-3 py-3 min-w-[180px]">
                            <input
                              type="text"
                              value={row.remedy}
                              onChange={(e) =>
                                updateRowField(rid, "remedy", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="Remedy"
                              disabled={isBusy}
                            />
                          </td>
                          <td className="px-3 py-3 min-w-[130px]">
                            <input
                              type="text"
                              value={row.fromDate}
                              onChange={(e) =>
                                updateRowField(rid, "fromDate", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="dd/MM/yyyy"
                              disabled={isBusy}
                            />
                          </td>
                          <td className="px-3 py-3 min-w-[100px]">
                            <input
                              type="text"
                              value={row.inTime}
                              onChange={(e) =>
                                updateRowField(rid, "inTime", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="IN"
                              disabled={isBusy}
                            />
                          </td>
                          <td className="px-3 py-3 min-w-[130px]">
                            <input
                              type="text"
                              value={row.toDate}
                              onChange={(e) =>
                                updateRowField(rid, "toDate", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="dd/MM/yyyy"
                              disabled={isBusy}
                            />
                          </td>
                          <td className="px-3 py-3 min-w-[100px]">
                            <input
                              type="text"
                              value={row.outTime}
                              onChange={(e) =>
                                updateRowField(rid, "outTime", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="OUT"
                              disabled={isBusy}
                            />
                          </td>
                          <td className="px-3 py-3 min-w-[100px]">
                            <input
                              type="text"
                              value={row.hrs}
                              onChange={(e) =>
                                updateRowField(rid, "hrs", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="Hrs"
                              disabled={isBusy}
                            />
                          </td>
                          <td className="px-3 py-3 min-w-[180px]">
                            <input
                              type="text"
                              value={row.maintenanceBy}
                              onChange={(e) =>
                                updateRowField(
                                  rid,
                                  "maintenanceBy",
                                  e.target.value
                                )
                              }
                              className={smallInputClass}
                              placeholder="Maintenance By"
                              disabled={isBusy}
                            />
                          </td>
                          <td className="px-6 py-4 text-center min-w-[120px]">
                            <button
                              onClick={() => handleSpareDetails(row)}
                              className={`font-semibold underline text-sm ${
                                isDark
                                  ? "text-blue-400 hover:text-blue-300"
                                  : "text-blue-600 hover:text-blue-800"
                              }`}
                              type="button"
                              disabled={isBusy}
                            >
                              {toInt(row.sparesCount, 0)}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(rid)}
                              onChange={() => toggleSelect(rid)}
                              disabled={isBusy || !rid}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div
                    className={`text-sm ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {paginationText1}
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      disabled={!canPrev1 || isBusy}
                      onClick={() => setPage1((p) => Math.max(1, p - 1))}
                      className={pagerBtn(canPrev1 && !isBusy)}
                    >
                      Prev
                    </button>
                    <div
                      className={`text-sm ${
                        isDark ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Page {page1} / {totalPages1}
                    </div>
                    <button
                      disabled={!canNext1 || isBusy}
                      onClick={() =>
                        setPage1((p) => Math.min(totalPages1, p + 1))
                      }
                      className={pagerBtn(canNext1 && !isBusy)}
                    >
                      Next
                    </button>
                    <select
                      value={limit1}
                      onChange={(e) => {
                        setLimit1(Number(e.target.value));
                        setPage1(1);
                      }}
                      disabled={isBusy}
                      className={inputClass}
                      style={{ width: 120 }}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n} / page
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-8">
            <h2
              className={`text-xl font-bold mb-3 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Job Card Entry Detail Listing
            </h2>

            <div className="overflow-x-auto rounded-lg border shadow-lg">
              {pageLoading && details.length === 0 ? (
                <div
                  className={`p-16 text-center ${
                    isDark ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <div
                    className={`text-lg font-medium ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Loading...
                  </div>
                </div>
              ) : pageRows2.length === 0 ? (
                <div
                  className={`p-16 text-center ${
                    isDark ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <div
                    className={`text-lg font-medium ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No Records found
                  </div>
                </div>
              ) : (
                <>
                  <table
                    className={`w-full min-w-[2400px] ${
                      isDark ? "bg-gray-800" : "bg-white"
                    }`}
                  >
                    <thead className={`${tableHeaderClass} text-white`}>
                      <tr>
                        {[
                          "S.No",
                          "Job Card No",
                          "Entry Date",
                          "Vehicle Name",
                          "Vehicle Reg No",
                          "Location",
                          "Hr/Km",
                          "Status",
                          "Nature Of Complaint",
                          "Complaint Attended By",
                          "Inspected By",
                          "Remedy",
                          "From Date",
                          "In Time",
                          "To Date",
                          "Out Time",
                          "Hrs",
                          "Maintenance By",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-6 py-4 text-left text-sm font-semibold"
                          >
                            {h}
                          </th>
                        ))}
                        <th className="px-6 py-4 text-center text-sm font-semibold">
                          Spare Details
                        </th>
                      </tr>
                    </thead>

                    <tbody
                      className={`${
                        isDark ? "divide-gray-700" : "divide-gray-200"
                      } divide-y`}
                    >
                      {pageRows2.map((row, idx) => (
                        <tr
                          key={row.jobCardEntryDetId || `${row.jobCardEntryId}-${idx}`}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td className={cellClass}>
                            {(page2 - 1) * limit2 + idx + 1}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-semibold ${
                              isDark ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            {row.jobCardNo || "-"}
                          </td>
                          <td className={cellClass}>{row.entryDate || "-"}</td>
                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleName || "-"}
                          </td>
                          <td className={cellClass}>
                            {row.vehicleRegNo || "-"}
                          </td>
                          <td className={cellClass}>{row.location || "-"}</td>
                          <td className={cellClass}>{row.hrKm || "-"}</td>
                          <td className="px-6 py-4 text-sm">
                            {row.status ? (
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  isDark
                                    ? "bg-yellow-900/40 text-yellow-200"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {row.status}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className={cellClass}>
                            {row.natureOfComplaint || "-"}
                          </td>
                          <td className={cellClass}>
                            {row.complaintAttendedBy || "-"}
                          </td>
                          <td className={cellClass}>
                            {row.inspectedBy || "-"}
                          </td>
                          <td className={cellClass}>{row.remedy || "-"}</td>
                          <td className={cellClass}>{row.fromDate || "-"}</td>
                          <td className={cellClass}>{row.inTime || "-"}</td>
                          <td className={cellClass}>{row.toDate || "-"}</td>
                          <td className={cellClass}>{row.outTime || "-"}</td>
                          <td className={cellClass}>{row.hrs || "-"}</td>
                          <td className={cellClass}>
                            {row.maintenanceBy || "-"}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => handleSpareDetails(row)}
                              className={`font-semibold underline text-sm ${
                                isDark
                                  ? "text-blue-400 hover:text-blue-300"
                                  : "text-blue-600 hover:text-blue-800"
                              }`}
                              type="button"
                              disabled={isBusy}
                            >
                              {clean(row.spareDetails) || "View"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div
                      className={`text-sm ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {paginationText2}
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        disabled={!canPrev2 || isBusy}
                        onClick={() => setPage2((p) => Math.max(1, p - 1))}
                        className={pagerBtn(canPrev2 && !isBusy)}
                      >
                        Prev
                      </button>
                      <div
                        className={`text-sm ${
                          isDark ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        Page {page2} / {totalPages2}
                      </div>
                      <button
                        disabled={!canNext2 || isBusy}
                        onClick={() =>
                          setPage2((p) => Math.min(totalPages2, p + 1))
                        }
                        className={pagerBtn(canNext2 && !isBusy)}
                      >
                        Next
                      </button>
                      <select
                        value={limit2}
                        onChange={(e) => {
                          setLimit2(Number(e.target.value));
                          setPage2(1);
                        }}
                        disabled={isBusy}
                        className={inputClass}
                        style={{ width: 120 }}
                      >
                        {[10, 20, 50, 100].map((n) => (
                          <option key={n} value={n}>
                            {n} / page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default JobCardEntryDetail;